import React, { useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CircularProgress, MenuItem, Select } from '@mui/material';
import { CalendarIcon, CheckIcon, GlobeIcon } from '../shared/Icons';
import { SlotPickerContainer } from './slotBooking/SlotBooking.styles';
import SlotTimeList from './slotBooking/SlotTimeList';
import { useSlots } from './slotBooking/useSlots';
import { DisplaySlot, resolveSlotConfig, SlotBookingConfig } from './slotBooking/slotTypes';
import {
  dayKeyInZone,
  formatSlotSummary,
  getTimezoneOptions,
  groupSlotsByDay,
  parseDayKey,
  toDisplaySlots,
} from './slotBooking/slotUtils';

interface SlotBookingData {
  field_name?: string;
  value?: string;
  slotConfig?: Partial<SlotBookingConfig>;
}

interface SlotBookingProps {
  data: SlotBookingData;
  onChange?: (value: Record<string, string>) => void;
  read_only?: boolean;
  /** false on the builder canvas, true in the rendered form. */
  mutable?: boolean;
}

const viewerTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const PanelMessage: React.FC<{ icon?: React.ReactNode; error?: boolean; children: React.ReactNode }> = ({
  icon,
  error,
  children,
}) => (
  <div className={`center-box${error ? ' error' : ''}`}>
    {icon}
    <span>{children}</span>
  </div>
);

const SlotBooking = React.forwardRef<HTMLInputElement, SlotBookingProps>((props, ref) => {
  const { field_name, value: initialValue, slotConfig } = props.data || {};
  const config = useMemo(() => resolveSlotConfig(slotConfig), [slotConfig]);
  const [searchParams] = useSearchParams();
  const isLive = props.mutable !== false;

  const [selectedIso, setSelectedIso] = useState<string>(initialValue || '');
  const [timezone, setTimezone] = useState<string>(viewerTimezone);
  const [selectedDayKey, setSelectedDayKey] = useState<string>(() =>
    initialValue ? dayKeyInZone(initialValue, viewerTimezone()) : '',
  );
  const [visibleMonth, setVisibleMonth] = useState<Date>(() =>
    initialValue ? new Date(initialValue) : new Date(),
  );
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => hiddenInputRef.current as HTMLInputElement);

  const timezones = useMemo(getTimezoneOptions, []);
  const { slots, loading, error, missingParams } = useSlots(config, visibleMonth, searchParams);
  const slotsByDay = useMemo(() => groupSlotsByDay(slots, timezone), [slots, timezone]);

  const daySlots: DisplaySlot[] = useMemo(
    () => toDisplaySlots(slotsByDay.get(selectedDayKey) || [], timezone, config.use24Hour),
    [slotsByDay, selectedDayKey, timezone, config.use24Hour],
  );

  const selectedDate = useMemo(
    () => (selectedDayKey ? parseDayKey(selectedDayKey) : null),
    [selectedDayKey],
  );

  const setupMessage = useMemo(() => {
    if (config.source === 'api' && !config.api.url) {
      return isLive ? 'Booking is not available right now.' : 'Add a slot API URL in the field properties.';
    }
    if (missingParams) {
      return isLive ? 'This booking link is incomplete.' : 'Open the form with the URL parameters to preview slots.';
    }
    return '';
  }, [config, isLive, missingParams]);

  const emitChange = (next: string) => {
    setSelectedIso(next);
    if (props.onChange && field_name) {
      props.onChange({ [field_name]: next });
    }
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDayKey(date ? format(date, 'yyyy-MM-dd') : '');
    if (selectedIso) emitChange('');
  };

  const hasSlots = (day: Date) => slotsByDay.has(format(day, 'yyyy-MM-dd'));
  const canDisableEmptyDays = !loading && !error && !setupMessage;
  const isSelectedDay = (day: Date) => format(day, 'yyyy-MM-dd') === selectedDayKey;

  const renderTimePanel = () => {
    if (setupMessage) {
      return <PanelMessage icon={<CalendarIcon size={30} />}>{setupMessage}</PanelMessage>;
    }
    if (!selectedDate) {
      if (error) return <PanelMessage error>{error}</PanelMessage>;
      if (loading) {
        return (
          <PanelMessage icon={<CircularProgress size={24} />}>Loading available slots…</PanelMessage>
        );
      }
      return (
        <PanelMessage icon={<CalendarIcon size={30} />}>
          {slots.length ? 'Pick a highlighted date to see available times.' : 'No slots available this month.'}
        </PanelMessage>
      );
    }

    return (
      <>
        <div className="time-header">
          <div>
            <div className="datepicker-title">{format(selectedDate, 'EEEE')}</div>
            <div className="datepicker-subtitle">{format(selectedDate, 'd MMMM yyyy')}</div>
          </div>
          <span className="time-count">
            {daySlots.length} slot{daySlots.length === 1 ? '' : 's'}
          </span>
        </div>
        {config.showTimezone && (
          <div className="timezone-select">
            <GlobeIcon size={15} />
            <Select
              variant="standard"
              disableUnderline
              value={timezone}
              onChange={(e) => setTimezone(e.target.value as string)}
              inputProps={{ 'aria-label': 'Timezone' }}
            >
              {timezones.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}
        <div className="time-slots-container">
          <SlotTimeList
            loading={loading && !daySlots.length}
            error={error}
            slots={daySlots}
            selectedIso={selectedIso}
            disabled={props.read_only}
            onSelect={(slot) => emitChange(slot.iso)}
          />
        </div>
        {selectedIso && (
          <div className="selected-slot-summary">
            <CheckIcon size={16} />
            <span>
              <strong>{formatSlotSummary(selectedIso, timezone, config.use24Hour)}</strong>
            </span>
          </div>
        )}
      </>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <SlotPickerContainer className="datepicker-wrapper">
        <div className="datepicker-grid">
          <div className="datepicker">
            <div className="datepicker-title">Select a date</div>
            <div className="datepicker-subtitle">Highlighted dates have open times.</div>
            <DateCalendar
              value={selectedDate}
              onChange={handleDateChange}
              onMonthChange={(month) => month && setVisibleMonth(month)}
              disablePast
              views={['day']}
              readOnly={props.read_only}
              shouldDisableDate={(day) => canDisableEmptyDays && !hasSlots(day) && !isSelectedDay(day)}
              className="datepicker-calendar"
              slotProps={{
                day: (dayProps) => ({
                  className: hasSlots(dayProps.day) ? 'has-slots' : '',
                }),
              }}
            />
          </div>

          <div className="datepicker time-section">{renderTimePanel()}</div>
        </div>
        <input ref={hiddenInputRef} type="hidden" name={field_name} value={selectedIso} />
      </SlotPickerContainer>
    </LocalizationProvider>
  );
});

SlotBooking.displayName = 'SlotBooking';

export default SlotBooking;
